/**
 * @fileoverview Theme Toggle Component
 *
 * Interactive toggle for switching between light, dark, and system themes.
 * Uses a popover menu to display theme options.
 *
 * @module shared/ThemeToggle
 */

import { useState } from 'react';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@xanectech/ui/components';
import { useTheme } from './ThemeProvider';
import { Sun, Moon, Monitor } from 'lucide-react';
import { cn } from '@xanectech/ui/lib';

/**
 * Theme Toggle Component
 *
 * Provides an interactive button that opens a popover menu for theme selection.
 * Displays an icon representing the current theme and allows users to switch
 * between light, dark, and system preferences.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes for styling
 * @returns {JSX.Element} Theme toggle popover with selection menu
 *
 * @example
 * ```tsx
 * // Basic usage
 * <ThemeToggle />
 *
 * // With custom className
 * <ThemeToggle className="mr-2" />
 *
 * // In a navbar
 * <nav>
 *   <Logo />
 *   <NavLinks />
 *   <ThemeToggle />
 * </nav>
 * ```
 *
 * @remarks
 * Features:
 * - Displays current theme icon (Sun, Moon, or Monitor)
 * - Popover menu with three theme options
 * - Highlights currently selected theme
 * - Closes automatically after selection
 * - Integrates with ThemeProvider context
 */
const ThemeToggle = ({ className }: { className?: string }) => {
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);

  /**
   * Returns the icon component for the current theme
   *
   * @returns {JSX.Element} Theme icon (Sun, Moon, or Monitor)
   */
  const getCurrentThemeIcon = () => {
    switch (theme) {
      case 'light':
        return <Sun className='h-4 w-4' />;
      case 'dark':
        return <Moon className='h-4 w-4' />;
      default:
        return <Monitor className='h-4 w-4' />;
    }
  };

  /**
   * Handles theme selection and closes the popover
   *
   * @param {('light' | 'dark' | 'system')} newTheme - Selected theme
   */
  const handleThemeChange = (newTheme: 'light' | 'dark' | 'system') => {
    setTheme(newTheme);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant='ghost' size='sm' aria-label='Toggle theme'>
          {getCurrentThemeIcon()}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(`w-48 p-2 bg-background border shadow-lg ${className}`)}
        align='end'
      >
        <div className='space-y-2'>
          <Button
            variant={theme === 'light' ? 'default' : 'ghost'}
            size='sm'
            className='w-full justify-start'
            onClick={() => handleThemeChange('light')}
          >
            <Sun className='h-4 w-4 mr-2' />
            Light
          </Button>
          <Button
            variant={theme === 'dark' ? 'default' : 'ghost'}
            size='sm'
            className='w-full justify-start'
            onClick={() => handleThemeChange('dark')}
          >
            <Moon className='h-4 w-4 mr-2' />
            Dark
          </Button>
          <Button
            variant={theme === 'system' ? 'default' : 'ghost'}
            size='sm'
            className='w-full justify-start'
            onClick={() => handleThemeChange('system')}
          >
            <Monitor className='h-4 w-4 mr-2' />
            System
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export { ThemeToggle };
